/**
 * Tiny header pill that lets ANY logged-in user (Owner or Worker) flip
 * persistent location sharing on/off. Uses the shared useLocationTracking
 * hook so the preference survives PWA close/reopen — vital for workers who
 * are otherwise lost from the map every time they background the app.
 *
 * UI: a single icon button.
 *   - OFF state: outlined "no location" icon, neutral
 *   - ON state: filled blue pin with a tiny green pulse dot
 *   - Tap → opens a small menu with status + toggle + permission help
 */
import React, { useState, useRef, useEffect } from 'react';
import { MapPin, MapPinOff, X } from 'lucide-react';
import { useLocationTracking } from '../hooks/useLocationTracking';

export const LocationShareToggle = () => {
  const { tracking, position, permissionDenied, enable, disable } = useLocationTracking();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const handleToggle = () => {
    if (tracking) {
      disable();
    } else {
      if (!navigator?.geolocation) return;
      // Prime the permission prompt explicitly before enabling so the user
      // sees an immediate browser dialog (not a silent failure).
      navigator.geolocation.getCurrentPosition(
        () => enable(),
        () => enable(),  // Even if denied, set the flag — they may grant later.
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`p-1.5 rounded-lg transition-colors relative ${
          tracking
            ? 'bg-blue-500/20 hover:bg-blue-500/30'
            : 'hover:bg-white/10'
        }`}
        title={tracking ? 'Sharing your location' : 'Location sharing off'}
        data-testid="location-share-toggle"
      >
        {tracking ? (
          <>
            <MapPin size={18} strokeWidth={2} className="text-blue-300" />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </>
        ) : (
          <MapPinOff size={18} strokeWidth={1.5} className="text-zinc-400" />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-zinc-200 p-4 z-50"
          data-testid="location-share-popover"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900">Share my location</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                {tracking
                  ? 'Your live position is visible to the owner so they can see your progress on the route.'
                  : 'Let the owner see where you are while you work — auto-records arrive/depart times.'}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-zinc-600 -mt-1 -mr-1"
              data-testid="close-location-share-popover"
            >
              <X size={16} />
            </button>
          </div>

          <button
            onClick={handleToggle}
            className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
              tracking
                ? 'bg-zinc-100 hover:bg-red-50 text-zinc-700 hover:text-red-700 border border-zinc-200 hover:border-red-200'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
            data-testid="location-share-action"
          >
            {tracking ? 'Stop sharing' : 'Start sharing'}
          </button>

          {tracking && position && (
            <p className="text-[10px] text-zinc-400 mt-2 text-center font-mono">
              {position.lat.toFixed(4)}, {position.lng.toFixed(4)}
              {position.accuracy ? ` · ±${Math.round(position.accuracy)}m` : ''}
            </p>
          )}

          {tracking && permissionDenied && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
              Browser location permission is denied — enable in browser settings, then re-open the app.
            </p>
          )}

          {tracking && (
            <p className="text-[10px] text-zinc-400 mt-2 text-center">
              This stays on when you close the app, so you don't have to re-enable it every shift.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationShareToggle;
